import {
  WorkflowStepType,
  type WorkflowConfigurationType,
  type WorkflowTemplateType,
} from "../schemas/index.ts";

type AssigneeFields = {
  assignee_user?: string | null;
  assignee_group?: string | null;
};

const sanitizeAssigneeFields = (assignee: AssigneeFields): AssigneeFields => {
  const sanitized = { ...assignee };

  if (sanitized.assignee_user === null) {
    delete sanitized.assignee_user;
  }

  if (sanitized.assignee_group === null) {
    delete sanitized.assignee_group;
  }

  return sanitized;
};

const sanitizeWorkflowStep = (
  step: WorkflowConfigurationType["steps"][string]
): WorkflowConfigurationType["steps"][string] => {
  const sanitizedStep = {
    ...step,
  } as WorkflowConfigurationType["steps"][string] & Record<string, unknown>;

  delete sanitizedStep.assignee;

  if (sanitizedStep.assignee_user === null) {
    delete sanitizedStep.assignee_user;
  }

  if (sanitizedStep.assignee_group === null) {
    delete sanitizedStep.assignee_group;
  }

  if (
    "assignment_filter" in sanitizedStep &&
    sanitizedStep.assignment_filter === null
  ) {
    delete sanitizedStep.assignment_filter;
  } else if (sanitizedStep.assignment_filter) {
    sanitizedStep.assignment_filter = Object.fromEntries(
      Object.entries(sanitizedStep.assignment_filter).map(
        ([attributeKey, attributeValues]) => [
          attributeKey,
          Object.fromEntries(
            Object.entries(attributeValues).map(([valueKey, assignee]) => [
              valueKey,
              sanitizeAssigneeFields(assignee as AssigneeFields),
            ])
          ),
        ]
      )
    );
  }

  if (
    sanitizedStep.type === WorkflowStepType.PARALLEL &&
    "branches" in sanitizedStep
  ) {
    sanitizedStep.branches = sanitizedStep.branches.map((branch) => ({
      ...branch,
      steps: Object.fromEntries(
        Object.entries(branch.steps).map(([stepId, nestedStep]) => [
          stepId,
          sanitizeWorkflowStep(nestedStep),
        ])
      ),
    }));
  }

  return sanitizedStep;
};

const sanitizeWorkflowConfig = (
  config: WorkflowConfigurationType
): WorkflowConfigurationType => ({
  ...config,
  steps: Object.fromEntries(
    Object.entries(config.steps).map(([stepId, step]) => [
      stepId,
      sanitizeWorkflowStep(step),
    ])
  ),
});

/**
 * Prepares a workflow template update payload for the v2 API.
 * v2 config schemas use optional assignee fields, not nullable ones.
 */
export const sanitizeWorkflowTemplateV2UpdatePayload = <
  T extends Partial<WorkflowTemplateType>,
>(
  data: T
): T => {
  if (!data.config) {
    return data;
  }

  return {
    ...data,
    config: sanitizeWorkflowConfig(data.config),
  };
};
